// core/services/followsService.ts — B4a: API-backed follow state.
//
// Thin fetch wrappers over /api/users/{username}/follow|following/. Hydrates the
// follow store (keyed by normalized handle) so hooks stay reactive. Handle→username:
// strip @, lowercase (the server also matches by handle, so @WayneTech→admin resolves).

import { useFollowStore, normHandle } from '@/core/stores/followStore';
import type { FollowState } from '@/core/models/follow';
import type { FollowedCreator, SuggestedProfile } from '@/core/models/account';
import { seedFollows } from '@/core/data/follows';
import { api } from '@/core/lib/apiClient';

export const followsService = {
  /**
   * Replace the follow set with the server's truth.
   *
   * Without this the store kept the three hardcoded handles it seeds at module init, so
   * Home's "Following" tab filtered the feed by seed data rather than by who the signed-in
   * user actually follows, and every Follow button showed its seeded label after a reload.
   * Mobile has had this since its port; web never grew one.
   */
  async hydrate(): Promise<void> {
    const r = await api<{ handles: string[] }>('/users/me/following/');
    const map: FollowState = {};
    for (const h of r.handles) map[normHandle(h)] = true;
    useFollowStore.getState().setFollows(map);
  },

  /**
   * Boot hydration for pending OUTGOING follow requests (to private accounts)
   * — the "Requested" button state. Mirrors hydrate() above and
   * usersService.hydrateMutes()'s reload problem: without this, the ···
   * menu / profile button forgets a pending request after a page reload.
   */
  async hydrateRequests(): Promise<void> {
    const r = await api<{ handles: string[] }>('/users/me/follow-requests/outgoing/');
    const map: FollowState = {};
    for (const h of r.handles) map[normHandle(h)] = true;
    useFollowStore.getState().setRequests(map);
  },

  async isFollowing(handle: string): Promise<{ following: boolean; requested: boolean }> {
    const r = await api<{ following: boolean; requested: boolean }>(
      `/users/${normHandle(handle)}/following/`,
    );
    useFollowStore.getState().setFollowing(handle, r.following);
    useFollowStore.getState().setRequested(handle, r.requested);
    return r;
  },

  /**
   * Three-state toggle: following a PUBLIC account still flips
   * following true/false as before; following a PRIVATE account instead
   * flips a pending request (`requested`). The response always carries both
   * flags, so callers that only care about one branch can ignore the other.
   */
  async toggleFollow(handle: string): Promise<{ following: boolean; requested: boolean }> {
    const r = await api<{ following: boolean; requested: boolean; followerCount: number }>(
      `/users/${normHandle(handle)}/follow/`,
      { method: 'POST' },
    );
    useFollowStore.getState().setFollowing(handle, r.following);
    useFollowStore.getState().setRequested(handle, r.requested);
    return { following: r.following, requested: r.requested };
  },

  /**
   * Every account the signed-in user follows, with the per-creator post
   * notification switch. Separate from `hydrate()` because that endpoint returns
   * bare handles, which cannot render a list with names and avatars.
   */
  async followedCreators(): Promise<FollowedCreator[]> {
    const r = await api<{ following: FollowedCreator[] }>('/users/me/following/notifications/');
    return r.following ?? [];
  },

  /** Turn post notifications on/off for one creator the user follows. */
  async setCreatorNotify(handle: string, notifyPosts: boolean): Promise<boolean> {
    const r = await api<{ handle: string; notifyPosts: boolean }>(
      `/users/${normHandle(handle)}/follow/notifications/`,
      { method: 'POST', body: { notifyPosts } },
    );
    return r.notifyPosts;
  },

  /**
   * 1.24.00 onboarding step 2: follow several accounts in one request —
   * `POST /users/follow-many/ {handles}`. ADDITIVE and idempotent (never
   * toggles): public targets are followed, private ones get a FollowRequest,
   * self/unknown/already-following are skipped. Max 20 handles (server cap).
   * Mirrors the store writes of `toggleFollow` so every Follow button reflects
   * the outcome without a refetch. Rejects on transport/HTTP failure so the
   * caller can toast; a `skipped` handle is NOT a failure.
   */
  async followMany(
    handles: string[],
  ): Promise<{ followed: string[]; requested: string[]; skipped: string[] }> {
    const r = await api<{ followed: string[]; requested: string[]; skipped: string[] }>(
      '/users/follow-many/',
      { method: 'POST', body: { handles } },
    );
    const store = useFollowStore.getState();
    for (const h of r.followed ?? []) store.setFollowing(h, true);
    for (const h of r.requested ?? []) store.setRequested(h, true);
    return { followed: r.followed ?? [], requested: r.requested ?? [], skipped: r.skipped ?? [] };
  },

  async followerCountDelta(handle: string): Promise<1 | -1> {
    const following = useFollowStore.getState().follows[normHandle(handle)] === true;
    return following ? 1 : -1;
  },

  /** Incoming pending follow requests, row-renderable — the Privacy settings
   * "Follow requests" manage list. */
  async incomingRequests(): Promise<SuggestedProfile[]> {
    const r = await api<{ requests: SuggestedProfile[] }>('/users/me/follow-requests/');
    return r.requests ?? [];
  },

  /** Accept an incoming follow request from `handle`. */
  async acceptRequest(handle: string): Promise<boolean> {
    const r = await api<{ accepted: boolean }>(
      `/users/${normHandle(handle)}/follow-requests/accept/`,
      { method: 'POST' },
    );
    return r.accepted;
  },

  /** Decline (delete) an incoming follow request from `handle`. */
  async declineRequest(handle: string): Promise<boolean> {
    const r = await api<{ declined: boolean }>(
      `/users/${normHandle(handle)}/follow-requests/decline/`,
      { method: 'POST' },
    );
    return r.declined;
  },

  _resetForTest(): void {
    const map: Record<string, boolean> = {};
    for (const entry of seedFollows) map[normHandle(entry.handle)] = true;
    useFollowStore.getState().setFollows(map);
    useFollowStore.getState().setRequests({});
  },
} as const;
